import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

// Mirrors the B6 boards contract (parking-app-api, ENG-2853). A board stores
// layout and view settings only, never an access grant.

const list = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((value) => value ?? []);

const BoardCompareSchema = z.object({
  mode: z.string(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
});

const BoardPresentationSchema = z.object({
  template: z.string(),
  version: z.number().int(),
});

const BoardSettingsSchema = z.object({
  range: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  compare: BoardCompareSchema.nullish(),
  location_ids: list(z.string()),
  presentation: BoardPresentationSchema.optional(),
});

const BoardWidgetQuerySchema = z.object({
  source: z.string(),
  metrics: list(z.string()),
  dimensions: list(z.string()),
  filters: list(
    z.object({
      field: z.string(),
      op: z.enum(['eq', 'in']),
      values: list(z.string()),
    })
  ),
  granularity: z.string().optional(),
  time_basis: z.string().optional(),
  order_by: z
    .object({ metric: z.string(), descending: z.boolean().optional() })
    .nullish(),
  location_ids: list(z.string()),
});

const BoardWidgetSchema = z.object({
  id: z.string().optional(),
  title: z.string(),
  query: BoardWidgetQuerySchema,
  viz: z.object({
    type: z.string(),
    options: z.record(z.string()).nullish(),
  }),
  layout: z.object({
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
  }),
});

export const BoardSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  is_default: z.boolean(),
  version: z.number().int(),
  updated_at: zodTimeStamp,
});
export type BoardSummary = z.infer<typeof BoardSummarySchema>;

export const BoardListSchema = z.object({
  boards: list(BoardSummarySchema),
  /** Absent on older APIs. */
  can_write: z.boolean().optional(),
});
export type BoardList = z.infer<typeof BoardListSchema>;

export const BoardSchema = z.object({
  id: z.string(),
  name: z.string(),
  settings: BoardSettingsSchema,
  widgets: list(BoardWidgetSchema),
  is_default: z.boolean(),
  version: z.number().int(),
  created_at: zodTimeStamp,
  updated_at: zodTimeStamp,
});
export type Board = z.infer<typeof BoardSchema>;
export type BoardWidget = z.infer<typeof BoardWidgetSchema>;
export type BoardSettings = z.infer<typeof BoardSettingsSchema>;
export type BoardPresentation = z.infer<typeof BoardPresentationSchema>;

/** What a write sends: the board's content, never ids or timestamps. */
export type BoardContent = {
  name: string;
  settings: BoardSettings;
  widgets: BoardWidget[];
};

const BoardContentSchema = z.object({
  name: z.string(),
  settings: BoardSettingsSchema,
  widgets: list(BoardWidgetSchema),
});

export const BOARD_TEMPLATES = [
  'single_lot',
  'multi_lot',
  'enforcement',
] as const;
export type BoardTemplate = (typeof BOARD_TEMPLATES)[number];

const TemplateBoardSchema = z.object({
  template: z.enum(BOARD_TEMPLATES).optional().catch(undefined),
  content: BoardContentSchema,
  can_write: z.boolean().optional(),
});

/**
 * GET /analytics/boards/default answers the caller's starred board, or an
 * unsaved board built for their footprint. Saving a template is a plain create
 * with its content.
 */
export const DefaultBoardSchema = z.union([
  z.object({
    board: BoardSchema,
    can_write: z.boolean().optional(),
    // The template stays reachable beside a starred board; older APIs omit it.
    template_board: TemplateBoardSchema.optional().catch(undefined),
  }),
  TemplateBoardSchema,
]);
export type DefaultBoard = z.infer<typeof DefaultBoardSchema>;

export const BOARD_VERSION_CONFLICT = 'board_version_conflict';
